import { CheckCircleIcon, ListChecksIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { useServiceOffer } from '../model/useServiceOffer';

import styles from './LockedTop4.module.css';
import { PaymentStepAction } from './PaymentStepAction';

/**
 * Что делает Karl после оплаты — пункты макета 03. Коды — ключи i18n.
 *
 * Формулировки «komplett geprüft» и «Detaillierte Prüfung» — тексты макета по
 * решению заказчика от 27.10.2026 (TASKS, Epic 58) и осознанное отступление от
 * CLAUDE.md §2: CarViQ.DE спрашивает автохаус, а не проверяет автомобиль. Ключи
 * перечислены в одном списке исключений теста фраз
 * (`shared/i18n/mockupTextExceptions.ts`); прецедентом для других экранов это
 * не является.
 */
const NEXT_ITEMS = ['analyse', 'check', 'negotiate', 'recommend'] as const;

/**
 * Карточка следующего шага — закрытый шаг 4 (FE-205, FE-586, MVP_PRD §33, §34;
 * DESIGN_SYSTEM §7.2, макет 03).
 *
 * Стоит после сетки витрины — там, где кончается бесплатная часть: данные
 * автомобилей открыты, а отбор четвёрки, вопросы автохаусам, торг и
 * рекомендация — работа Karl, за которую платят (Epic 58).
 *
 * Чего здесь нет и быть не может: превью четырёх карточек и счётчиков вроде
 * «уже подобрано 4 варианта». Четвёрка отбирается **после** оплаты (§34.3) —
 * показывать её до неё нечем.
 *
 * **Цена услуги — только из конфигурации** (`payments.price_cents`, хардкод
 * запрещён), и рядом с ней условия вознаграждения по результату: цена услуги
 * честна целиком (DESIGN_SYSTEM §8, MVP_PRD §35.5, правило FE-210). Нет
 * конфигурации — нет и строки цены, а не выдуманная сумма.
 *
 * **Главное действие одно** (DESIGN_SYSTEM §2.2, §5.1): «Jetzt Top-4
 * aktivieren» — единственная красная кнопка экрана. Вошедшего она ведёт на
 * оплату, гостю открывает окно входа: регистрация вызывается ровно здесь, при
 * попытке оплатить (MVP_PRD §41.1). Это переход, а не исполнение, поэтому на
 * телефоне её несёт полоса оболочки рядом с Karl (FE-585).
 */
export function LockedTop4({ searchId }: { searchId: string }) {
  const { t } = useTranslation();
  const { price, feeTerms } = useServiceOffer();

  return (
    <section className={styles.card} aria-labelledby="locked-top4-title">
      <div className={styles.main}>
        <span className={styles.icon} aria-hidden="true">
          <ListChecksIcon size={24} />
        </span>
        <div className={styles.content}>
          <h2 id="locked-top4-title" className={styles.title}>
            {t('showcase.next.title')}
          </h2>

          <ul className={styles.list}>
            {NEXT_ITEMS.map((code) => (
              <li key={code} className={styles.item}>
                <CheckCircleIcon size={16} weight="fill" aria-hidden="true" />
                <span>{t(`showcase.next.item.${code}`)}</span>
              </li>
            ))}
          </ul>

          {feeTerms === null ? null : <p className={styles.fee}>{feeTerms}</p>}
        </div>
      </div>

      <div className={styles.aside}>
        {/* Вошедшего ведёт на оплату, гостю открывает окно входа; пока не
            известно, кто перед нами, кнопки нет вовсе (PaymentStepAction). */}
        <PaymentStepAction searchId={searchId} className={styles.action}>
          {t('showcase.next.action')}
        </PaymentStepAction>

        {price === null ? null : (
          <p className={`${styles.price} tabular`}>{t('showcase.next.price', { price })}</p>
        )}
      </div>
    </section>
  );
}
